/**
 * Pure helpers used by LiveDetailScreen. Side-effect free, so safe to
 * sit outside the component and share across future render-branch
 * extractions (LiveDetailLiveShell, LiveDetailEndedDesktop, …).
 *
 * Extracted in slice 3 of the LiveDetailScreen decomposition.
 */

/**
 * Pull the 11-char video id out of a YouTube URL. Supports both
 * `youtube.com/watch?v=...`, `youtube.com/embed/...`, and the short
 * `youtu.be/...` forms. Returns `null` for non-YouTube URLs and `null`
 * inputs (so the screen can treat «no embed available» uniformly).
 */
export const getYouTubeVideoId = (url: string | null): string | null => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
};

/**
 * CORE-765 — how a failed `getLiveById` should be interpreted.
 *
 *   - `not_found`     — a genuine 404 on a public link. The ONLY terminal
 *                       «Эфир не найден» case.
 *   - `invite_invalid`— CORE-721: an invite link (`?invite=`) the BE rejected
 *                       with 403/404. The live exists; the link is off.
 *   - `link_invalid`  — a stale cohost preview token (404).
 *   - `subscribers_only` — CORE-980: a SubscribersOnly live the caller may not
 *                       open. The BE detail endpoint answers 403 with the code
 *                       `live_subscribers_only` (the same code the join gate
 *                       already uses), so the live exists — the caller just
 *                       isn't a subscriber. It gets its own «доступно
 *                       подписчикам» panel, never the «deleted» copy.
 *   - `transient`     — 401 / bare 403 / 5xx / network. On the first open the
 *                       app can fire the fetch with a stale JWT before auth
 *                       finished initialising, and the BE answers 401 for a
 *                       perfectly valid public live. Treating that as
 *                       «not found» is exactly the reported prod defect, so
 *                       it must stay recoverable (retry / poll), never terminal.
 */
export type LiveLoadFailureKind =
  | 'not_found'
  | 'invite_invalid'
  | 'link_invalid'
  | 'subscribers_only'
  | 'agapella_required'
  | 'transient';

/** CORE-980 — the BE error code that marks a SubscribersOnly access denial. */
export const LIVE_SUBSCRIBERS_ONLY_CODE = 'live_subscribers_only';

/**
 * CORE-1778 — тот же код, что `AGAPELLA_SUBSCRIPTION_REQUIRED` в agapellaGate;
 * не импортом — модуль тянет за собой окно и аналитику в чистые хелперы.
 */
const AGAPELLA_SUBSCRIPTION_REQUIRED = 'agapella_subscription_required';

export function classifyLiveLoadError(
  err: any,
  opts: { hasPreviewToken: boolean; hasInviteCode: boolean },
): LiveLoadFailureKind {
  const status: number | undefined = err?.status ?? err?.response?.status;
  // The BE error body may carry `code` (camelCase) or `Code` (PascalCase),
  // either at the top level (api.ts throws the parsed body directly) or under
  // `response.data`.
  const code: string | undefined =
    err?.code ?? err?.Code ?? err?.response?.data?.code ?? err?.response?.data?.Code;
  // CORE-721 — an invite link that the BE rejects is a broken LINK, not a
  // missing эфир; keep this ahead of the bare-status checks below.
  if (opts.hasInviteCode && (status === 403 || status === 404)) return 'invite_invalid';
  if (opts.hasPreviewToken && status === 404) return 'link_invalid';
  // A real 404 on a plain public link is the only thing that means «deleted».
  if (status === 404) return 'not_found';
  // CORE-980 — a 403 that explicitly names the subscribers-only gate is a
  // truthful «exists, but for subscribers», not a recoverable auth race.
  if (status === 403 && code === LIVE_SUBSCRIBERS_ONLY_CODE) return 'subscribers_only';
  // CORE-1778 — закрыто подпиской Agapēlla: окно, а не «временная ошибка» с опросом.
  if (status === 403 && code === AGAPELLA_SUBSCRIPTION_REQUIRED) return 'agapella_required';
  // 401 (stale token before auth settled), bare 403, 5xx, and network errors
  // (no status) are all recoverable — never a terminal «not found».
  return 'transient';
}

/**
 * CORE-765 — gate for the terminal «Эфир не найден» panel. It may only show
 * for a genuine 404, and only once auth has finished initialising (so a
 * first-open 401 racing ahead of auth can't masquerade as a deletion) and no
 * live has loaded. `invite_invalid` / `link_invalid` render their own dedicated
 * panels, not this one.
 */
export function shouldShowLiveNotFound(args: {
  hasLive: boolean;
  failureKind: LiveLoadFailureKind | null;
  authReady: boolean;
}): boolean {
  if (args.hasLive) return false;
  if (!args.authReady) return false;
  return args.failureKind === 'not_found';
}

/**
 * CORE-883 (QA 23.10) — программа, в которую вести с «не найдена».
 *
 * Уведомление о сессии программы может пережить саму сессию: отмена снимает
 * только непрочитанные строки ленты, а прочитанные «скоро начнётся»,
 * «перенесён» и «запись готова» остаются со ссылкой на удалённую. Программу
 * передаёт переход из ленты (параметр навигации) и ссылка push'а
 * (`?programId=` — на вебе прямой заход по ссылке параметр не наполняет,
 * поэтому адрес — второй источник, как у `preview` и `chat`).
 *
 * Только положительное целое: битый id — не повод уводить человека.
 */
export function readLiveProgramId(param: unknown, fromQuery?: string | null): number | null {
  for (const raw of [param, fromQuery]) {
    if (raw == null || raw === '') continue;
    const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
    if (Number.isInteger(n) && n > 0) return n;
  }
  return null;
}
